import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

const requestEvent = 'mediagraph:prepare-tour-navigation';
const navigationControls = '.introjs-nextbutton, .introjs-prevbutton, .introjs-bullets a';

// Intro.js increments its step before awaiting onBeforeChange. Stop a second
// UI navigation before that increment, while allowing Escape/Close to exit.
export function useTourTransitionLock() {
    const pending = useRef(false);
    const controls = useRef(new Map());
    const finish = useCallback(token => {
        if(token && pending.current !== token) return;
        pending.current = false;
        controls.current.forEach((value, element) => {
            if(value == null) element.removeAttribute('aria-disabled');
            else element.setAttribute('aria-disabled', value);
        });
        controls.current.clear();
    }, []);
    const begin = useCallback(token => {
        pending.current = token;
        document.querySelectorAll(navigationControls).forEach(element => {
            if(!controls.current.has(element)) controls.current.set(element, element.getAttribute('aria-disabled'));
            element.setAttribute('aria-disabled', 'true');
        });
    }, []);
    useEffect(() => {
        const guard = event => {
            if(!pending.current) return;
            const close = event.target?.closest?.('.introjs-skipbutton');
            const navigation = event.type === 'click' ? event.target?.closest?.(navigationControls) :
                ['ArrowLeft', 'ArrowRight', 'Enter', 'NumpadEnter'].includes(event.code || event.key) && !close;
            if(navigation) { event.preventDefault(); event.stopImmediatePropagation(); }
        };
        document.addEventListener('click', guard, true);
        window.addEventListener('keydown', guard, true);
        return () => {
            document.removeEventListener('click', guard, true);
            window.removeEventListener('keydown', guard, true);
            finish();
        };
    }, [finish]);
    return {begin, finish};
}

// The compact drawer acknowledges requests only after its opening/closing
// animation. Promise completion is cancelled when the tour or drawer exits.
export function useTourDrawerNavigation() {
    const [open, setOpenState] = useState(false);
    const state = useRef({open: false, settled: true});
    const pending = useRef(new Set());
    const tourSignal = useRef();
    const setOpen = useCallback(value => {
        const next = !!value;
        if(state.current.open !== next) state.current = {open: next, settled: false};
        setOpenState(next);
    }, []);
    const afterOpenChange = useCallback(visible => {
        if(state.current.open !== visible) return;
        state.current.settled = true;
        pending.current.forEach(waiter => {
            if(waiter.open === visible) waiter.finish(true);
        });
    }, []);
    const closeOnScopeChange = useCallback(() => {
        // Initial route/filter hydration may finish after a tour requests the
        // sidebar. Its navigation owns the drawer until exit/abort.
        if(!tourSignal.current || tourSignal.current.aborted) setOpen(false);
    }, [setOpen]);

    useLayoutEffect(() => {
        const prepare = event => {
            const {sidebar, signal} = event.detail;
            tourSignal.current = signal;
            event.detail.handled = true;
            event.detail.waitUntil(new Promise(resolve => {
                let timer;
                const waiter = {open: sidebar, finish: result => {
                    clearTimeout(timer);
                    signal?.removeEventListener('abort', abort);
                    pending.current.delete(waiter);
                    resolve(result);
                }};
                const abort = () => waiter.finish(false);
                if(signal?.aborted) return resolve(false);
                pending.current.add(waiter);
                signal?.addEventListener('abort', abort, {once: true});
                timer = setTimeout(abort, 3000);
                setOpen(sidebar);
                if(state.current.settled && state.current.open === sidebar) waiter.finish(true);
            }));
        };
        window.addEventListener(requestEvent, prepare);
        return () => {
            window.removeEventListener(requestEvent, prepare);
            pending.current.forEach(waiter => waiter.finish(false));
        };
    }, [setOpen]);

    return {open, setOpen, afterOpenChange, closeOnScopeChange};
}

function waitForNavigation(predicate, signal) {
    if(signal?.aborted) return Promise.resolve(false);
    if(predicate()) return Promise.resolve(true);
    return new Promise(resolve => {
        let timer;
        const finish = result => {
            clearTimeout(timer);
            observer.disconnect();
            signal?.removeEventListener('abort', abort);
            resolve(result);
        };
        const abort = () => finish(false);
        const ready = () => { if(predicate()) finish(true); };
        const observer = new MutationObserver(ready);
        observer.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded']});
        signal?.addEventListener('abort', abort, {once: true});
        timer = setTimeout(abort, 10_000);
        ready();
    });
}

export async function prepareTourNavigation(sidebar, signal) {
    if(signal?.aborted) return false;
    // Header tours can mount before the organization and Explore route finish
    // loading. Do not initialize Intro.js against an absent navigation tree.
    if(sidebar && !await waitForNavigation(() => document.querySelector('#mobile-sidebar-toggle, #left-sidebar-btn'), signal)) return false;
    const waits = [];
    const detail = {sidebar, signal, handled: false, waitUntil: promise => waits.push(promise)};
    window.dispatchEvent(new CustomEvent(requestEvent, {detail}));
    if(detail.handled) return (await Promise.all(waits)).every(Boolean) && !signal?.aborted;
    if(document.getElementById('mobile-sidebar-toggle')) return false;

    const toggle = document.getElementById('left-sidebar-btn');
    if(!sidebar || !toggle || toggle.getAttribute('aria-expanded') !== 'false') return true;
    toggle.click();
    return waitForNavigation(() => document.getElementById('left-sidebar-btn')?.getAttribute('aria-expanded') === 'true', signal);
}

export function expandTourPanel(id, force = false) {
    const panel = document.getElementById(id);
    if(!force && panel?.querySelector('.ant-collapse-content-active')) return;
    (panel?.querySelector('.collapse-heading-control') || panel?.querySelector('.ant-collapse-header-text'))?.click();
}

export function positionTourTooltip(element) {
    const target = element?.querySelector?.('.collapse-heading-control') || element;
    document.querySelector('.introjs-tooltip')?.setAttribute('data-tour-edge',
        target && target.getBoundingClientRect().top < window.innerHeight / 2 ? 'bottom' : 'top');
    document.querySelector('.introjs-tooltiptext')?.setAttribute('tabindex', '0');
}
